import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useMemo } from 'react';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { getPreviewColor } from '../margin/marginToolbarUtils';
import { usePreviewBase } from '../shared/usePreviewBase';

interface UseRingPreviewOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest | undefined;
}

const keyForRing = (lowerMm: number, upperMm: number): string =>
    `ring:${lowerMm},${upperMm}`;

export function useRingPreview({
    environment,
    inputRoi,
}: UseRingPreviewOptions) {
    const {
        isPreviewing,
        setIsPreviewing,
        updateView,
        beginOperation,
        setResult,
        consumeResult,
        abortInFlight,
        cleanup,
    } = usePreviewBase({ environment });

    const handlePreviewRing = useCallback(
        async (lowerMm: number, upperMm: number) => {
            if (
                !environment?.structureSet ||
                !inputRoi?.texture ||
                !environment.image
            ) {
                return;
            }

            if (upperMm <= lowerMm) {
                cleanup();
                setIsPreviewing(false);
                return;
            }

            const signal = beginOperation();
            setIsPreviewing(true);

            // TODO: improve these kind of hacky rendering solutions should be removed and
            // heavy computation should be lifted away from blocking the main thread for smooth rendering
            await waitForUI();

            try {
                const ringTexture = await SDFOperations.applyRing(
                    inputRoi.texture,
                    lowerMm,
                    upperMm
                );

                if (signal.aborted) {
                    ringTexture.delete();
                    return;
                }

                const previewObj = new CustomTargetObject(
                    environment.image,
                    ringTexture,
                    getPreviewColor(inputRoi.color)
                );
                previewObj.renderOptions = { fillAlpha: 0.18 };
                setResult(previewObj, keyForRing(lowerMm, upperMm));
            } catch (e) {
                console.error('Ring preview error:', e);
                cleanup();
                enqueueSnackbar(`Ring preview failed: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            } finally {
                setIsPreviewing(false);
            }
        },
        [
            environment,
            inputRoi,
            beginOperation,
            setResult,
            cleanup,
            setIsPreviewing,
        ]
    );

    const consumeRingPreview = useCallback(
        (lowerMm: number, upperMm: number): ContourTexture | undefined =>
            consumeResult(keyForRing(lowerMm, upperMm)),
        [consumeResult]
    );

    return useMemo(
        () => ({
            isPreviewing,
            handlePreviewRing,
            consumeRingPreview,
            abortInFlight,
            cleanup,
            updateView,
        }),
        [
            isPreviewing,
            handlePreviewRing,
            consumeRingPreview,
            abortInFlight,
            cleanup,
            updateView,
        ]
    );
}
